import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ClipLoader } from 'react-spinners';
import { API_BASE_URL } from '../../../../config/api';
import {
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaClock,
  FaExclamationTriangle,
} from 'react-icons/fa';

import GlobalHeader from '../../../../components/GlobalHeader.jsx';
import EventImage from '../../../../components/EventImage.jsx';
import NoEventsMessage from '../../../../components/common/NoEventsMessage.jsx';
import '../../Rehost/css/RehostEventForm.css';

const computeStatus = (isApproved, eventDate, endDate) => {
  if (isApproved === null || isApproved === undefined) return 'pending';
  if (isApproved === false) return 'rejected';

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const start = eventDate ? new Date(eventDate) : null;
  const end = endDate ? new Date(endDate) : null;

  if (end && end < today) return 'completed';
  if (start && start <= today && (!end || end >= today)) return 'ongoing';
  return 'approved';
};

const STATUS_LABELS = {
  pending: 'Pending Approval',
  approved: 'Approved',
  rejected: 'Rejected',
  ongoing: 'Ongoing',
  completed: 'Completed',
};

const formatDate = (dateString) => {
  if (!dateString) return 'Date not specified';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Date not specified';
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return 'Date not specified';
  }
};

const MyEvents = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const fetchEvents = async () => {
      try {
        setLoading(true);
        setError(null);

        const token = localStorage.getItem('token');
        if (!token) {
          navigate('/');
          return;
        }

        const response = await fetch(`${API_BASE_URL}/organiser/all-events`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        });

        if (!response.ok) {
          if (response.status === 401 || response.status === 403) {
            localStorage.removeItem('token');
            localStorage.removeItem('userId');
            localStorage.removeItem('user');
            navigate('/');
            return;
          }
          throw new Error(`Server error: ${response.status}`);
        }

        const data = await response.json();
        const eventsData = Array.isArray(data) ? data : [];

        const normalised = eventsData.map((event) => ({
          id: event.event_id || '',
          event_name: event.event_name || 'Unnamed Event',
          location: event.location || 'Location not specified',
          date: event.event_date || '',
          end_date: event.end_date || '',
          time: event.start_time || 'Time not specified',
          status: computeStatus(event.is_approved, event.event_date, event.end_date),
          file_url: event.cover_image_url || '/default-event-image.jpg',
          background: event.background_images_array || null,
        }));

        if (isMounted) {
          setEvents(normalised);
          setError(null);
        }
      } catch (err) {
        if (isMounted) {
          setError(err.message || 'Failed to load events. Please try again later.');
          setEvents([]);
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchEvents();
    return () => { isMounted = false; };
  }, [navigate]);

  const renderContent = () => {
    if (loading) {
      return (
        <div className="rehost-loading-container">
          <ClipLoader color="#3a7bd5" loading={loading} size={50} />
          <p className="rehost-loading-text">Loading your events...</p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="rehost-error-container">
          <FaExclamationTriangle size={50} color="#e53935" />
          <h3 className="rehost-title">Error Loading Events</h3>
          <p className="rehost-error-message">{error}</p>
          <button
            className="rehost-view-button"
            onClick={() => { setError(null); setLoading(true); setEvents([]); }}
          >
            Try Again
          </button>
        </div>
      );
    }

    if (events.length === 0) {
      return <NoEventsMessage />;
    }

    return (
      <div className="rehost-card-grid">
        {events.map((event) => (
          <div key={event.id} className="rehost-card">
            <div className="rehost-card-image-container">
              <EventImage
                image={event.file_url}
                coverimage={event.file_url}
                alt={event.event_name}
                className="rehost-card-image"
                loading="eager"
              />
            </div>
            <div className="rehost-card-content">
              <h3 className="rehost-card-title">{event.event_name}</h3>
              <div className="rehost-card-details">
                <p className="rehost-detail-row">
                  <FaMapMarkerAlt className="rehost-detail-icon-location" /> {event.location}
                </p>
                <p className="rehost-detail-row">
                  <FaCalendarAlt className="rehost-detail-icon-date" /> {formatDate(event.date)}
                </p>
                <p className="rehost-detail-row">
                  <FaClock className="rehost-detail-icon-time" /> {event.time}
                </p>
              </div>
            </div>
            <div className="rehost-card-bottom">
              <span className={`rehost-status rehost-status--${event.status}`}>
                {STATUS_LABELS[event.status] || event.status}
              </span>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="rehost-container">
      <GlobalHeader
        title="My Events"
        subtitle="View all your hosted events and their statuses"
        backTo="/requestcard"
        logoutTo="/"
        onLogout={() => {
          localStorage.removeItem('token');
          localStorage.removeItem('userId');
          localStorage.removeItem('user');
          navigate('/');
        }}
      />
      {renderContent()}
    </div>
  );
};

export default MyEvents;
